import React from 'react';

const baseClasses = 'inline-flex items-center font-medium rounded-full';

const sizeClasses = {
  small: 'px-2 py-0.5 text-xs',
  medium: 'px-2.5 py-0.5 text-sm',
  large: 'px-3 py-1 text-sm'
};

// Call sites write both the long and the short form of every size.
const sizeAliases = { sm: 'small', md: 'medium', lg: 'large' };

const variantClasses = {
  default: 'bg-gray-100 text-gray-800',
  primary: 'bg-primary-100 text-primary-800',
  secondary: 'bg-gray-100 text-gray-600',
  accent: 'bg-accent-100 text-accent-800',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
  ghost: 'bg-transparent text-gray-600',
  // "custom" means the caller supplies its own colours through className. It
  // has to be a real entry rather than a lookup miss, so the miss below can
  // safely fall back to the default colours.
  custom: ''
};

const Badge = ({
  variant = 'default',
  size = 'medium',
  children,
  className = '',
  ...props
}) => {
  // Unrecognised values fall back rather than leaving the badge with no colour
  // or no padding at all. `??` on the variant so that custom's empty string is
  // not itself treated as a miss.
  const sizeClass = sizeClasses[sizeAliases[size] || size] || sizeClasses.medium;
  const variantClass = variantClasses[variant] ?? variantClasses.default;

  return (
    <span
      className={`${baseClasses} ${sizeClass} ${variantClass} ${className}`.replace(/\s+/g, ' ').trim()}
      {...props}
    >
      {children}
    </span>
  );
};

const StatusBadge = ({ 
  status, 
  size = 'medium', 
  className = '', 
  ...props 
}) => {
  const statusConfig = {
    active: { variant: 'success', label: 'Active' },
    inactive: { variant: 'secondary', label: 'Inactive' },
    pending: { variant: 'warning', label: 'Pending' },
    approved: { variant: 'success', label: 'Approved' },
    rejected: { variant: 'danger', label: 'Rejected' },
    draft: { variant: 'secondary', label: 'Draft' },
    published: { variant: 'success', label: 'Published' },
    archived: { variant: 'secondary', label: 'Archived' },
    processing: { variant: 'info', label: 'Processing' },
    completed: { variant: 'success', label: 'Completed' },
    failed: { variant: 'danger', label: 'Failed' }
  };
  
  const config = statusConfig[status] || { variant: 'default', label: status };
  
  return (
    <Badge 
      variant={config.variant} 
      size={size} 
      className={className}
      {...props}
    >
      {config.label}
    </Badge>
  );
};

export default Badge;
export { StatusBadge };